import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import {infiniteQueryOptions, type QueryKey, useInfiniteQuery} from '@github-ui/react-query'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {parseLinkHeader} from '../../utils/parse-link-header'
import type {ConversationCommentsPayload} from '../payloads/conversation-comments'

export function conversationCommentsQueryKey(pathName: string): QueryKey {
  return [PageData.conversationComments, pathName]
}

export type ConversationCommentsPage = {
  data: ConversationCommentsPayload['items']
  nextUrl: string | null
}

export function conversationCommentsApiUrl(pathName: string): string {
  return `${pathName}/page_data/${PageData.conversationComments}`
}

interface UseInfiniteConversationCommentsQueryOpts {
  pathName: string
  enabled?: boolean
}

function conversationCommentsQueryOptions(pathName: string) {
  return infiniteQueryOptions({
    queryKey: conversationCommentsQueryKey(pathName),
    initialPageParam: conversationCommentsApiUrl(pathName),
    queryFn: async ({pageParam}): Promise<ConversationCommentsPage> => {
      const result = await reactFetchJSON(pageParam)
      if (!result.ok) throw new Error(`Failed to fetch conversation comments from ${pageParam}: HTTP ${result.status}`)

      const json = (await result.json()) as ConversationCommentsPayload
      const linkHeader = result.headers.get('Link')
      const nextUrl = parseLinkHeader(linkHeader, 'next')

      return {data: json.items, nextUrl}
    },
    getNextPageParam: lastPage => lastPage.nextUrl,
    select: ({pages}) => pages.flatMap(page => page.data),
  })
}

export function useInfiniteConversationCommentsQuery({pathName, enabled}: UseInfiniteConversationCommentsQueryOpts) {
  return useInfiniteQuery({
    ...conversationCommentsQueryOptions(pathName),
    enabled,
  })
}
